import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as actions from "redux/actions/auth";
import bgImage from "assets/images/bg-sign-in-basic.jpeg";
import cosmaLogo from "assets/images/logos/Cosmaupload.png";
import { useMaterialUIController, setLayout } from "context";

const S = {
  root: {
    fontFamily: "'Inter', sans-serif",
    background: `url(${bgImage}) center center / cover no-repeat`,
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
  },
  box: {
    background: "#fff",
    borderRadius: "24px",
    padding: "48px 40px",
    boxShadow: "0 2px 24px rgba(123,94,167,0.15)",
    width: "100%",
    maxWidth: "480px",
  },
  back: {
    display: "inline-flex", alignItems: "center", gap: "6px",
    color: "#7b5ea7", fontSize: "0.83rem", fontWeight: 600,
    cursor: "pointer", border: "none", background: "none",
    fontFamily: "'Inter', sans-serif", marginBottom: "20px", padding: 0,
    textDecoration: "none",
  },
  logo: { textAlign: "center", marginBottom: "20px" },
  logoImg: { height: "40px" },
  typeBadge: {
    display: "inline-block", background: "#7b5ea715",
    border: "1.5px solid #7b5ea7", color: "#7b5ea7",
    fontSize: "0.72rem", fontWeight: 800, padding: "3px 12px",
    borderRadius: "100px", textTransform: "uppercase",
    letterSpacing: "0.08em", marginBottom: "12px",
  },
  h3: { fontSize: "1.55rem", fontWeight: 800, color: "#1a0a2e", margin: "0 0 6px 0" },
  sub: { color: "#7a6895", fontSize: "0.87rem", marginBottom: "28px", lineHeight: 1.6 },
  fieldWrap: { marginBottom: "16px" },
  label: {
    display: "block", fontSize: "0.76rem", fontWeight: 700,
    textTransform: "uppercase", letterSpacing: ".06em",
    color: "#7a6895", marginBottom: "6px",
  },
  input: (focus) => ({
    width: "100%", padding: "13px 16px", background: "#f3eeff",
    border: `1.5px solid ${focus ? "#7b5ea7" : "rgba(123,94,167,0.18)"}`,
    borderRadius: "10px", color: "#1a0a2e", fontSize: "0.95rem",
    fontFamily: "'Inter', sans-serif", outline: "none",
    boxSizing: "border-box", transition: "border-color .2s",
  }),
  row: { display: "flex", gap: "12px" },
  btn: {
    width: "100%", padding: "15px", borderRadius: "10px",
    fontSize: "1rem", fontWeight: 800, cursor: "pointer",
    border: "none", marginTop: "8px",
    background: "linear-gradient(135deg,#7b5ea7,#9c27b0)", color: "#fff",
    boxShadow: "0 4px 20px rgba(123,94,167,0.35)", fontFamily: "'Inter', sans-serif",
    transition: "opacity .2s",
  },
  infoBox: {
    background: "#f3eeff", border: "1.5px solid rgba(123,94,167,0.2)",
    borderRadius: "10px", padding: "12px 16px", marginBottom: "22px",
    fontSize: "0.81rem", color: "#5a4080", lineHeight: 1.6,
  },
  links: { marginTop: "20px", fontSize: "0.83rem", color: "#7a6895", textAlign: "center" },
  a: { color: "#7b5ea7", textDecoration: "none", fontWeight: 600 },
  hint: { fontSize: "0.76rem", color: "#9a88b5", marginTop: "4px" },
  toast: {
    position: "fixed", bottom: "32px", left: "50%", transform: "translateX(-50%)",
    background: "linear-gradient(135deg,#7b5ea7,#9c27b0)", color: "#fff",
    padding: "12px 28px", borderRadius: "100px", fontWeight: 700,
    fontSize: "0.9rem", zIndex: 9999, whiteSpace: "nowrap",
  },
};

function SingleUserRegister() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [, uiDispatch] = useMaterialUIController();
  const { registerLoading } = useSelector((state) => state.auth);
  const [focused, setFocused] = useState(null);
  const [toast, setToast] = useState({ msg: "", show: false });
  const [form, setForm] = useState({ name: "", email: "", password: "", confirmPassword: "" });
  const [pwError, setPwError] = useState("");

  useEffect(() => { setLayout(uiDispatch, "page"); }, []);

  const showToast = (msg) => {
    setToast({ msg, show: true });
    setTimeout(() => setToast((t) => ({ ...t, show: false })), 3000);
  };

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    if (e.target.name === "confirmPassword" || e.target.name === "password") setPwError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) {
      setPwError("Passwords do not match.");
      return;
    }
    if (form.password.length < 8) {
      setPwError("Password must be at least 8 characters.");
      return;
    }
    showToast("Creating your account…");
    dispatch(actions.register({ ...form, type: "user" }));
  };

  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <div style={S.root}>
        <div style={S.box}>
          <button style={S.back} onClick={() => navigate("/auth/register")}>
            ← Back to account types
          </button>

          <div style={S.logo}>
            <img src={cosmaLogo} alt="COSMA" style={S.logoImg} />
          </div>

          <span style={S.typeBadge}>Single User</span>
          <h3 style={S.h3}>Create Your Account</h3>
          <p style={S.sub}>
            Full access to the COSMA portal — progress reports, activity charts, and personal dashboard.
          </p>

          <div style={S.infoBox}>
            ✓ &nbsp;Complete portal access &nbsp;·&nbsp; ✓ &nbsp;Progress tracking &nbsp;·&nbsp; ✓ &nbsp;Game reports
          </div>

          <div style={{
            background: "#fff8e1", border: "1.5px solid #f9a825",
            borderRadius: "10px", padding: "14px 16px", marginBottom: "22px",
            fontSize: "0.83rem", color: "#6d4c00", lineHeight: 1.6,
          }}>
            <strong>⚠ Subscription required.</strong> You must have an active COSMA subscription before registering. Use the <strong>same email address</strong> you used when purchasing.{" "}
            <a
              href="https://cosmaapp.com/#pricing"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#7b5ea7", fontWeight: 700 }}
            >
              View plans →
            </a>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={S.fieldWrap}>
              <label style={S.label}>Full Name</label>
              <input
                type="text" name="name" placeholder="Jane Smith" required
                value={form.name} onChange={handleChange}
                style={S.input(focused === "name")}
                onFocus={() => setFocused("name")} onBlur={() => setFocused(null)}
              />
            </div>

            <div style={S.fieldWrap}>
              <label style={S.label}>Email Address</label>
              <input
                type="email" name="email" placeholder="jane@example.com" required
                value={form.email} onChange={handleChange}
                style={S.input(focused === "email")}
                onFocus={() => setFocused("email")} onBlur={() => setFocused(null)}
              />
            </div>

            <div style={S.fieldWrap}>
              <label style={S.label}>Password</label>
              <input
                type="password" name="password" placeholder="Min. 8 characters" required
                value={form.password} onChange={handleChange}
                style={S.input(focused === "password")}
                onFocus={() => setFocused("password")} onBlur={() => setFocused(null)}
              />
            </div>

            <div style={S.fieldWrap}>
              <label style={S.label}>Confirm Password</label>
              <input
                type="password" name="confirmPassword" placeholder="Re-enter password" required
                value={form.confirmPassword} onChange={handleChange}
                style={S.input(focused === "confirmPassword")}
                onFocus={() => setFocused("confirmPassword")} onBlur={() => setFocused(null)}
              />
              {pwError && <p style={{ ...S.hint, color: "#c0392b" }}>{pwError}</p>}
            </div>

            <button type="submit" disabled={registerLoading} style={{ ...S.btn, opacity: registerLoading ? 0.7 : 1 }}>
              {registerLoading ? "Creating Account…" : "Create Account →"}
            </button>
          </form>

          <p style={S.links}>
            Already have an account?{" "}
            <a href="/auth/login" style={S.a}>Sign In →</a>
            &nbsp;|&nbsp;
            <a href="/auth/register" style={S.a}>Choose different type</a>
          </p>
        </div>
      </div>
      {toast.show && <div style={S.toast}>{toast.msg}</div>}
    </>
  );
}

export default SingleUserRegister;
